import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Camera, X, RotateCcw, Check, AlertTriangle, ShieldCheck, SwitchCamera, Aperture } from "lucide-react";
import { Surface, AButton, Pill } from "@/components/ios";
import { analyzeImageQuality } from "@/lib/imageQuality";
import { compressImage } from "@/lib/image";
import { listCameras, videoConstraints } from "@/lib/camera";

// In-app camera using getUserMedia with an on-device quality gate.
export default function CameraCapture({ open, onClose, onCapture, target }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [cameras, setCameras] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [error, setError] = useState("");
  const [shot, setShot] = useState(null); // { dataUrl, quality }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setShot(null);
    setError("");
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints(deviceId, facing),
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        const cams = await listCameras();
        if (!cancelled) setCameras(cams);
      } catch (e) {
        setError("Camera unavailable. Use the file picker instead.");
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open, facing, deviceId]);

  const cycleLens = () => {
    if (cameras.length < 2) return;
    const idx = cameras.findIndex((c) => c.deviceId === deviceId);
    setDeviceId(cameras[(idx + 1) % cameras.length].deviceId);
  };
  const lensLabel = cameras.find((c) => c.deviceId === deviceId)?.label;

  const capture = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setBusy(true);
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const dataUrl = await compressImage(c.toDataURL("image/jpeg", 0.9));
    const quality = await analyzeImageQuality(dataUrl);
    setShot({ dataUrl, quality });
    setBusy(false);
  };

  const confirm = () => {
    onCapture(shot.dataUrl);
    onClose();
  };

  if (!open) return null;

  const q = shot?.quality;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] grid place-items-center p-4"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)" }}
      data-testid="camera-modal"
    >
      <Surface className="w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2 font-semibold" style={{ color: "var(--text)" }}>
            <Camera size={16} className="accent-text" /> Capture · {target?.label || "Photo"}
          </div>
          <button data-testid="camera-close" onClick={onClose} className="text-secondary"><X size={18} /></button>
        </div>

        <div className="relative bg-black" style={{ aspectRatio: "3 / 4" }}>
          {error ? (
            <div className="absolute inset-0 grid place-items-center text-center p-6 text-white text-sm">{error}</div>
          ) : shot ? (
            <img src={shot.dataUrl} alt="preview" className="absolute inset-0 h-full w-full object-cover" data-testid="camera-preview" />
          ) : (
            <video ref={videoRef} playsInline muted className="absolute inset-0 h-full w-full object-cover" data-testid="camera-video" />
          )}

          {shot && q && !q.ok && (
            <div className="absolute top-3 left-3 right-3 flex flex-wrap gap-2">
              {q.tooDark && <Pill tone="warning"><AlertTriangle size={11} /> Low light</Pill>}
              {q.tooBright && <Pill tone="warning"><AlertTriangle size={11} /> Over-exposed</Pill>}
              {q.blurry && <Pill tone="danger"><AlertTriangle size={11} /> Looks blurry</Pill>}
            </div>
          )}
          {shot && q && q.ok && (
            <div className="absolute top-3 left-3">
              <Pill tone="success"><ShieldCheck size={11} /> Sharp & well-lit</Pill>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-3">
          {shot ? (
            <>
              <AButton variant="text" testid="camera-retake" onClick={() => setShot(null)}>
                <RotateCcw size={15} className="inline mr-1" /> Retake
              </AButton>
              <AButton testid="camera-use" tone={q && !q.ok ? "warning" : "accent"} onClick={confirm}>
                <Check size={16} className="inline mr-1.5" /> {q && !q.ok ? "Use anyway" : "Use photo"}
              </AButton>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <button
                  data-testid="camera-flip"
                  onClick={() => { setDeviceId(null); setFacing((f) => (f === "environment" ? "user" : "environment")); }}
                  className="flex items-center gap-1.5 text-sm text-secondary"
                  disabled={!!error}
                >
                  <SwitchCamera size={16} /> Flip
                </button>
                {cameras.length > 1 && (
                  <button
                    data-testid="camera-lens"
                    onClick={cycleLens}
                    className="flex items-center gap-1.5 text-sm text-secondary"
                    disabled={!!error}
                  >
                    <Aperture size={16} /> {lensLabel || "Lens"}
                  </button>
                )}
              </div>
              <button
                data-testid="camera-shutter"
                onClick={capture}
                disabled={!!error || busy}
                className="h-14 w-14 rounded-full grid place-items-center disabled:opacity-40"
                style={{ background: "var(--accent)" }}
              >
                <div className="h-11 w-11 rounded-full border-4 border-white" />
              </button>
              <div className="w-12" />
            </>
          )}
        </div>
      </Surface>
    </motion.div>
  );
}
